import React from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Activity, CheckSquare, AlertCircle } from 'lucide-react';
import { RackStructure, compareSegments } from '../rack-builder/types';
import { LocationHeatmapDto } from './types';
import { ActiveMoveBin } from './heatmapMoveTypes';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface InteractiveRackBodyProps {
    rackStructure: RackStructure;
    heatmapData: Map<string, LocationHeatmapDto>;
    activeBinCode: string | null;
    activeIsBatch: boolean;
    selectedBinCodes: Set<string>;
    onToggleSelect: (binCode: string, mode: 'single' | 'add' | 'range') => void;
    onBinContextMenu?: (binCode: string, x: number, y: number) => void;
    /** Bin codes that are currently empty — shown with subtle green ring as candidate destinations. */
    emptyBinCodes?: Set<string>;
    /** Bins with pending/in-progress moves — orange ring + tooltip. Map binCode → list of activities. */
    activeMoveBins?: Map<string, ActiveMoveBin[]>;
    /** Title shown above the grid (for dual-view, distinguishes "Kilde" vs "Destinasjon"). */
    title?: string;
    /** Optional label badge shown next to title. */
    labelBadge?: React.ReactNode;
}

/**
 * Rack grid rendering (shelves × verticals) with draggable/droppable cells.
 * Does NOT own a DndContext — the parent provides it. Used by both
 * single-rack and dual-rack views.
 */
export const InteractiveRackBody: React.FC<InteractiveRackBodyProps> = ({
    rackStructure,
    heatmapData,
    activeBinCode,
    activeIsBatch,
    selectedBinCodes,
    onToggleSelect,
    onBinContextMenu,
    emptyBinCodes,
    activeMoveBins,
    title,
    labelBadge
}) => {
    const { t } = useTranslation();
    const locationMap = new Map<string, any>();
    rackStructure.locations.forEach((loc) => {
        locationMap.set(`${loc.vertical}-${loc.shelf}`, loc);
    });

    const sortedShelves = [...rackStructure.shelves].sort((a, b) => compareSegments(b, a));
    const sortedVerticals = [...rackStructure.verticals].sort(compareSegments);

    if (rackStructure.locations.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-500">
                <p>{t('manager.modules.locationHeatmap.grid.noLocationsToShow', {}, 'Ingen lokasjoner å vise')}</p>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col">
            {title && (
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                    <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
                    {labelBadge}
                </div>
            )}

            <div
                className="w-full flex-1 border border-gray-300 bg-white p-3 overflow-auto"
                style={{ userSelect: 'none' }}
            >
                <div className="flex mb-2 sticky top-0 bg-white z-10 border-b border-gray-200 pb-1">
                    <div className="w-16 flex-shrink-0" />
                    {sortedVerticals.map(vertical => (
                        <div
                            key={vertical}
                            className="w-32 text-center font-semibold text-sm text-gray-700 flex-shrink-0"
                        >
                            V{/^\d+$/.test(vertical) ? vertical.padStart(2, '0') : vertical}
                        </div>
                    ))}
                </div>

                <div className="space-y-1">
                    {sortedShelves.map((shelf) => (
                        <div key={shelf} className="flex items-center">
                            <div className="w-16 text-right pr-3 font-semibold text-sm text-gray-700 flex-shrink-0 sticky left-0 bg-white z-5">
                                H{/^\d+$/.test(shelf) ? shelf.padStart(2, '0') : shelf}
                            </div>
                            {sortedVerticals.map(vertical => {
                                const location = locationMap.get(`${vertical}-${shelf}`);
                                const heatData = location ? heatmapData.get(location.binCode) : undefined;
                                const isSelected = !!location && selectedBinCodes.has(location.binCode);
                                const isEmpty = !!location && emptyBinCodes?.has(location.binCode);
                                const activities = location ? activeMoveBins?.get(location.binCode) : undefined;
                                return (
                                    <DraggableDroppableBin
                                        key={`${vertical}-${shelf}`}
                                        location={location}
                                        heatData={heatData}
                                        isDragSource={activeBinCode === location?.binCode}
                                        isDragging={!!activeBinCode}
                                        isSelected={isSelected}
                                        dragIsBatch={activeIsBatch}
                                        isEmpty={isEmpty ?? false}
                                        activities={activities}
                                        onToggleSelect={onToggleSelect}
                                        onContextMenu={onBinContextMenu}
                                    />
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

interface DraggableDroppableBinProps {
    location: any;
    heatData: LocationHeatmapDto | undefined;
    isDragSource: boolean;
    isDragging: boolean;
    isSelected: boolean;
    dragIsBatch: boolean;
    isEmpty: boolean;
    activities?: ActiveMoveBin[];
    onToggleSelect: (binCode: string, mode: 'single' | 'add' | 'range') => void;
    onContextMenu?: (binCode: string, x: number, y: number) => void;
}

const DraggableDroppableBin: React.FC<DraggableDroppableBinProps> = ({
    location,
    heatData,
    isDragSource,
    isDragging,
    isSelected,
    isEmpty,
    activities,
    onToggleSelect,
    onContextMenu
}) => {
    const { t } = useTranslation();
    const binCode = location?.binCode ?? '';

    const { attributes, listeners, setNodeRef: setDragRef } = useDraggable({
        id: binCode,
        disabled: !location
    });

    const { isOver, setNodeRef: setDropRef } = useDroppable({
        id: binCode,
        disabled: !location
    });

    const setBothRefs = (el: HTMLDivElement | null) => {
        setDragRef(el);
        setDropRef(el);
    };

    const handleClick = (e: React.MouseEvent) => {
        if (!location) return;
        e.stopPropagation();
        const mode: 'single' | 'add' | 'range' =
            e.ctrlKey || e.metaKey ? 'add' : e.shiftKey ? 'range' : 'single';
        onToggleSelect(location.binCode, mode);
    };

    const handleContextMenu = (e: React.MouseEvent) => {
        if (!location || !onContextMenu) return;
        e.preventDefault();
        e.stopPropagation();
        onContextMenu(location.binCode, e.clientX, e.clientY);
    };

    if (!location) {
        return (
            <div className="w-32 h-24 relative border border-dashed border-gray-200 flex flex-col items-center justify-center p-1.5 text-sm flex-shrink-0">
                <div className="text-gray-400 text-[8px]">-</div>
            </div>
        );
    }

    const hasActivity = !!activities && activities.length > 0;

    // Priority: drag source > drop target > selected > ACTIVE MOVE > empty (candidate) > default
    // Active-move (orange ring) beats "empty" because a reserved bin cannot be used
    // even if it looks empty (stock is locked by a pending move).
    const borderClass = isDragSource
        ? 'border-blue-500 border-4 shadow-xl ring-2 ring-blue-300'
        : isOver && isDragging
          ? 'border-green-500 border-4 shadow-xl ring-2 ring-green-300'
          : isSelected
            ? 'border-purple-500 border-4 ring-2 ring-purple-300'
            : hasActivity
              ? 'border-orange-500 border-2 ring-2 ring-orange-200 hover:shadow-lg'
              : isEmpty
                ? 'border-green-400 border-dashed border-2 hover:shadow-lg hover:border-green-500 hover:border-solid'
                : 'border-gray-300 hover:shadow-lg hover:border-blue-300';

    // Build tooltip text. Header explains the activity count (since one item may
    // have several concurrent activities — e.g. 3 picking reservations), followed
    // by one line per activity for the detailed breakdown.
    const activityTooltip = hasActivity
        ? (() => {
            const uniqueItems = new Set(activities!.map(a => a.itemNo).filter(Boolean)).size;
            const header = activities!.length === 1
                ? t('manager.modules.locationHeatmap.activityTooltip.single', {}, '1 aktivitet:')
                : uniqueItems > 0 && uniqueItems < activities!.length
                    ? (uniqueItems === 1
                        ? t('manager.modules.locationHeatmap.activityTooltip.distributedSingular', { '0': activities!.length, '1': uniqueItems }, `${activities!.length} aktiviteter fordelt på ${uniqueItems} vare:`)
                        : t('manager.modules.locationHeatmap.activityTooltip.distributedPlural', { '0': activities!.length, '1': uniqueItems }, `${activities!.length} aktiviteter fordelt på ${uniqueItems} varer:`))
                    : t('manager.modules.locationHeatmap.activityTooltip.multiple', { '0': activities!.length }, `${activities!.length} aktiviteter:`);
            const lines = activities!.map(a => {
                const qty = a.quantity ? ` · ${a.quantity}` : '';
                const item = a.itemNo ? ` ${a.itemNo}` : '';
                return `• ${a.description || a.role}${item}${qty}`;
            });
            return [header, ...lines].join('\n');
        })()
        : undefined;

    // Count badge tooltip — short, tells the user what the number represents.
    const countBadgeTooltip = hasActivity && activities!.length > 1
        ? t('manager.modules.locationHeatmap.activityTooltip.countBadge', { '0': activities!.length }, `${activities!.length} aktiviteter pågår på denne lokasjonen — hover for detaljer`)
        : undefined;

    return (
        <div
            ref={setBothRefs}
            {...attributes}
            {...listeners}
            onClick={handleClick}
            onContextMenu={handleContextMenu}
            title={activityTooltip}
            className={`w-32 h-24 relative border transition-all flex flex-col items-center justify-center p-1.5 text-xs flex-shrink-0 cursor-grab active:cursor-grabbing ${borderClass}`}
            style={heatData ? { backgroundColor: heatData.heatColor } : undefined}
        >
            {isSelected && (
                <div className="absolute top-0.5 right-0.5 bg-purple-600 text-white rounded-full w-4 h-4 flex items-center justify-center shadow">
                    <CheckSquare className="w-2.5 h-2.5" />
                </div>
            )}
            {hasActivity && !isSelected && (
                <div className="absolute top-0.5 right-0.5 bg-orange-500 text-white rounded-full w-4 h-4 flex items-center justify-center shadow" title={activityTooltip}>
                    <AlertCircle className="w-2.5 h-2.5" />
                </div>
            )}
            {hasActivity && activities!.length > 1 && (
                <div
                    className="absolute top-0.5 left-0.5 bg-orange-500 text-white rounded-full h-4 px-1 flex items-center justify-center shadow text-[9px] font-bold"
                    title={countBadgeTooltip}
                >
                    {activities!.length}
                </div>
            )}
            <div className="font-mono font-semibold text-xs text-gray-800">
                {location.binCode}
            </div>
            {heatData ? (
                <>
                    <div className="flex items-center justify-center space-x-1 mt-1 h-5">
                        <Activity className="w-3 h-3 text-white" />
                        <span className="text-[10px] font-bold text-white">{t('manager.modules.locationHeatmap.cell.level', { '0': heatData.heatLevel }, `Niv ${heatData.heatLevel}`)}</span>
                    </div>
                    <div className="text-[10px] text-white font-medium">
                        {t('manager.modules.locationHeatmap.cell.transShort', { '0': heatData.transactionCount }, `${heatData.transactionCount} trans.`)}
                    </div>
                </>
            ) : hasActivity ? (
                <div className="text-[10px] text-orange-700 font-semibold mt-0.5">{t('manager.modules.locationHeatmap.cell.moveInProgress', {}, 'Pågår flytt')}</div>
            ) : isEmpty ? (
                <div className="text-[10px] text-green-600 font-semibold mt-0.5">{t('manager.modules.locationHeatmap.cell.emptyAvailable', {}, 'Tom · ledig')}</div>
            ) : (
                <div className="text-[10px] text-gray-400 mt-0.5">{t('manager.modules.locationHeatmap.cell.noActivity', {}, 'Ingen aktivitet')}</div>
            )}
        </div>
    );
};
